import React, { useEffect, useState, useCallback } from "react";
import InputIterator from '../../components/common/inputIterator';
import PrimarySideBar from "../../components/common/sideBar/primarySideBar";
import AddButtonSection from "../../components/common/AddButtonSection";
import { DevTool } from "@hookform/devtools";
import { useForm } from "react-hook-form";
import { addOrEditPatientVisit as formRender } from "../../utils/serviceData";
import Loader from "../../components/common/loader";
import { showToast } from "../../components/common/toast";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { commonGetApi, commonPostApi, commonPutApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";

export default function AddOrEditPatientVisit({
  formName = "editPatientVisit",
  ...props
}) {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoader] = useState(false);
  const [purposes, setPurposes] = useState([]);
  const [renderInput, setRenderInput] = useState({ formRender });
  const [patientVisitAllDetails, setPatientVisitAllDetails] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const selectedPurpose = watch("purpose");

  const onSubmit = async (data) => {
    if (Object.entries(dirtyFields).length > 0) {
      setLoader(true);
      try {
        // Get patient_visit_id from props
        const patientVisitId = props?.patientVisit?.patient_visit_id || props?.id;

        if (!patientVisitId) {
          showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.PATIENT_VISIT_ID_REQUIRED);
          setLoader(false);
          return;
        }

        // Prepare the data payload
        const payload = {
          ...data,
          patient_visit_id: patientVisitId,
          visit_date: data.date ? new Date(data.date).toISOString().split('T')[0] : undefined,
        };

        // Remove fields that shouldn't be sent to API
        delete payload.date; // Use visit_date instead
        delete payload.patient_name; // This is read-only

        console.log(patientVisitAllDetails);



        // Call the edit API endpoint via PUT
        const response = await commonPutApi(API_ENDPOINTS.EDIT_PATIENT_VISIT_DETAILS(patientVisitId), payload);

        if (response?.data?.success || response?.status === 200) {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.VISIT.UPDATED);
          props?.setVisible?.(false, true);
          props?.initCallBack?.();
          reset();
        } else {
          throw new Error(response?.data?.message || COMMON_MSG.ERROR.FAILED_TO_UPDATE_VISIT);
        }
      } catch (error) {
        console.error("Error updating patient visit:", error);
        showToast(TOAST_TYPE.ERROR, error.message || COMMON_MSG.ERROR.FAILED_TO_UPDATE_VISIT);
      } finally {
        setLoader(false);
      }
    }
  };

  // Remove patientVisitAllDetails from dependencies to prevent infinite loop
  const callData = useCallback(async () => {
    if (dataLoaded) return; // Prevent multiple calls

    setLoader(true);
    try {
      // Fetch examiners
      const examinerResp = await commonGetApi(API_ENDPOINTS.GET_EXAMINER_DETAILS);
      const examinerUsers = examinerResp?.data?.data || [];

      // Update examined_by options
      setRenderInput((prev) => ({
        ...prev,
        formRender: (prev?.formRender || formRender).map((field) => {
          if (field?.name === "examined_by") {
            return {
              ...field,
              options: examinerUsers.map((user) => ({
                label: user?.user_name,
                value: user?.user_id,
              })),
            };
          }
          return field;
        }),
      }));

      // Optionally prefill date
      setValue("date", new Date());

      // Fetch purposes
      const lookupData = { lookup_type_name: "VISIT_PURPOSE" };
      const purposeResp = await commonPostApi(API_ENDPOINTS.SEARCH_LOOKUP_DETAILS, lookupData);
      const purposeList = purposeResp?.data?.data || [];
      setPurposes(purposeList);

      // Update purpose options
      setRenderInput((prev) => ({
        ...prev,
        formRender: (prev?.formRender || formRender).map((field) => {
          if (field?.name === "purpose") {
            return {
              ...field,
              options: purposeList.map((purpose) => ({
                label: purpose?.name,
                value: purpose?.lookup_id,
              })),
            };
          }
          return field;
        }),
      }));

      // If patientId is available via props, fetch all patient visits for primary_visit_id options
      const patientId = props?.patientId ?? props?.id;
      if (patientId) {
        const visitDetails = {
          searchValue: "",
          patient_id: patientId,
        };
        const patientVisit = await commonPostApi(
          API_ENDPOINTS.GET_ALL_PATIENT_VISIT_DETAILS,
          visitDetails
        );
        const visitsList = patientVisit?.data?.data || [];
        setPatientVisitAllDetails(visitsList);

        // Ensure patient name is available; fetch if missing
        let patientName = props?.patientVisit?.patient_name;
        if (!patientName) {
          try {
            const patientResp = await commonPostApi(API_ENDPOINTS.SEARCH_PATIENT_DETAILS, {
              searchValue: "",
              patient_id: patientId,
            });


            const patientData = Array.isArray(patientResp?.data?.data)
              ? patientResp?.data?.patient?.[0]
              : patientResp?.data?.patient;

            patientName = patientData?.patient_name || [patientData?.first_name, patientData?.last_name].filter(Boolean).join(" ");
          } catch (e) {
            // ignore
          }
        }

        if (patientName) {
          // Inject a disabled patient name field at the top if not already present
          setRenderInput((prev) => {
            const exists = (prev?.formRender || []).some((f) => f?.name === "patient_name");
            if (exists) return prev;
            return {
              ...prev,
              formRender: [
                {
                  type: "text",
                  name: "patient_name",
                  label: "Patient",
                  fieldLabel: "patient_name",
                  disabled: true,
                },
                ...(prev?.formRender || formRender),
              ],
            };
          });
          setValue("patient_name", patientName);
        }

        // Prefill form values if patientVisit prop is provided
        const visit = props?.patientVisit;
        

        if (visit) {
          // Date
          if (visit?.visit_date) {
            const dateValue = new Date(visit.visit_date);
            setValue("date", dateValue);
          }

          // Purpose dropdown expects lookup_id; map from name if necessary
          if (visit?.purpose) {
            let purposeValue = visit.purpose;
            if (typeof purposeValue === "string") {
              const matchedPurpose = purposeList.find((p) => p?.name === purposeValue);
              if (matchedPurpose) purposeValue = matchedPurpose.lookup_id;
            }
            setValue("purpose", purposeValue);
            

            // If this is a follow-up, set primary_visit_id immediately as well
            const initialVisitId = purposeList.find((p) => p?.name === "Initial Visit")?.lookup_id;
            if (initialVisitId && purposeValue !== initialVisitId) {
              // Use visitsList instead of patientVisitAllDetails state
              const primaryVisitOptions = visitsList
                .filter((pv) => pv?.purpose === initialVisitId)
                .map((pv) => ({ label: pv?.patient_visit_code, value: pv?.patient_visit_id }));

              // Inject field now so UI shows it without waiting for the purpose effect
              setRenderInput((prev) => {
                const withoutPrimary = (prev?.formRender || []).filter((f) => f.name !== "primary_visit_id");
                const purposeIndex = withoutPrimary.findIndex((f) => f.name === "purpose");
                const insertIndex = purposeIndex >= 0 ? purposeIndex + 1 : 2;
                const field = {
                  type: "dropdown",
                  name: "primary_visit_id",
                  label: "Primary Visit Id",
                  fieldLabel: "primary_visit_id",
                  options: primaryVisitOptions,
                  rules: { required: "Primary visit id is required" },
                };
                return {
                  ...prev,
                  formRender: [
                    ...withoutPrimary.slice(0, insertIndex),
                    field,
                    ...withoutPrimary.slice(insertIndex),
                  ],
                };
              });

              // Set the value to existing primary_visit_id or fallback to first option
              const selectedPrimaryId = visit?.primary_visit_id || primaryVisitOptions?.[0]?.value;
              if (selectedPrimaryId) setValue("primary_visit_id", selectedPrimaryId);
            }
          }

          // examined_by dropdown expects user_id; prefer examined_by id; fallback by name
          if (visit?.examined_by) {
            setValue("examined_by", visit.examined_by);
          } else if (visit?.examiner_name) {
            const matchedExaminer = examinerUsers.find((u) => u?.user_name === visit.examiner_name);
            if (matchedExaminer) setValue("examined_by", matchedExaminer.user_id);
          }

          // If this is a follow-up, set primary_visit_id if exists
          if (visit?.primary_visit_id) {
            setValue("primary_visit_id", visit.primary_visit_id);
          }
        }
      }

      setDataLoaded(true); // Mark data as loaded
    } catch (error) {
      console.error("Error fetching visit data:", error);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_VISIT_DATA);
    }
    setLoader(false);
  }, [props?.patientId, props?.id, props?.patientVisit, setValue, dataLoaded]);

  const buttons = [
    {
      type: "primarySuccessButton",
      label: loading ? "Saving..." : "Save Changes",
      disabled: Object.keys(dirtyFields).length === 0 || loading,
      class: "col-6",
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "secondaryButton",
      label: "Discard",
      class: "col-4",
      disabled: loading,
      onClick: () => {
        props?.setVisible?.(false);
      },
    },
  ];

  useEffect(() => {
    setVisible(props?.visible);
  }, [props?.visible]);

  useEffect(() => {
    if (props?.mode === "edit" && (props?.id || props?.patientId || props?.patientVisit?.patient_visit_id)) {
      callData();
    }
  }, [callData, props?.mode, props?.id, props?.patientId, props?.patientVisit?.patient_visit_id]);

  // Reset dataLoaded when component props change (for new patient visits)
  useEffect(() => {
    setDataLoaded(false);
  }, [props?.id, props?.patientId, props?.patientVisit?.patient_visit_id]);

  // Dynamically manage Primary Visit ID based on selected purpose
  useEffect(() => {
    if (!purposes?.length || !dataLoaded) return; // Only run after initial data load

    const selectedPurposeName = purposes?.find((p) => p.lookup_id === selectedPurpose)?.name;
    const initialVisitId = purposes?.find((p) => p.name === "Initial Visit")?.lookup_id;

    if (selectedPurposeName && selectedPurposeName !== "Initial Visit") {
      const primaryVisitOptions = patientVisitAllDetails
        .filter((visit) => visit?.purpose === initialVisitId)
        .map((visit) => ({
          label: visit?.patient_visit_code,
          value: visit?.patient_visit_id,
        }));

      const primary_visit_id = {
        type: "dropdown",
        name: "primary_visit_id",
        label: "Primary Visit Id",
        fieldLabel: "primary_visit_id",
        options: primaryVisitOptions,
        rules: {
          required: "Primary visit id is required",
        },
      };

      setRenderInput((prev) => {
        const withoutPrimary = (prev?.formRender || []).filter((f) => f.name !== "primary_visit_id");
        const purposeIndex = withoutPrimary.findIndex((f) => f.name === "purpose");
        const insertIndex = purposeIndex >= 0 ? purposeIndex + 1 : 2;
        return {
          ...prev,
          formRender: [
            ...withoutPrimary.slice(0, insertIndex),
            primary_visit_id,
            ...withoutPrimary.slice(insertIndex),
          ],
        };
      });
    } else {
      setRenderInput((prev) => ({
        ...prev,
        formRender: (prev?.formRender || []).filter((f) => f.name !== "primary_visit_id"),
      }));
      setValue("primary_visit_id", "");
    }
  }, [selectedPurpose, purposes, patientVisitAllDetails, setValue, dataLoaded]);

  return (
    <React.Fragment>
      <PrimarySideBar
        visible={visible}
        onHide={() => props?.setVisible?.(false)}
        header={<div className="sideBar-header">Edit Patient Visit</div>}
      >
        {loading ? (
          <Loader />
        ) : (
          <React.Fragment>
            <InputIterator
              control={control}
              errors={errors}
              formRender={renderInput?.formRender}
            />
            <div className="pt-3">
              <AddButtonSection buttons={buttons} />
            </div>
          </React.Fragment>
        )}
      </PrimarySideBar>
      <DevTool control={control} />
    </React.Fragment>
  );
}